import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { adminApi, formatApiError } from "../auth";
import { Plus, Copy, Eye, EyeOff, Trash2, Pencil, Search, Upload } from "lucide-react";

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = (q = "") => {
    setLoading(true);
    adminApi.get("/products", { params: q ? { search: q } : {} })
      .then((r) => setProducts(r.data))
      .catch((e) => setErr(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const doDelete = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try { await adminApi.delete(`/products/${id}`); load(search); } catch (e) { alert(formatApiError(e)); }
  };
  const doDuplicate = async (id) => {
    try { await adminApi.post(`/products/${id}/duplicate`); load(search); } catch (e) { alert(formatApiError(e)); }
  };
  const doToggle = async (id) => {
    try { await adminApi.post(`/products/${id}/toggle-visibility`); load(search); } catch (e) { alert(formatApiError(e)); }
  };

  return (
    <div data-testid="admin-products-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 style={{ fontSize: 28 }}>Products</h1>
          <p className="vd-muted">{products.length} tyres in catalogue</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/admin/products/import" className="vd-btn-secondary" data-testid="admin-bulk-import-btn"><Upload size={16} /> Bulk Import CSV</Link>
          <Link to="/admin/products/new" className="vd-btn-primary" data-testid="admin-add-product-btn"><Plus size={16} /> Add Product</Link>
        </div>
      </div>

      <div className="vd-card p-4 mb-5 flex items-center gap-3" data-testid="admin-products-search">
        <Search size={16} color="var(--text-muted)" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load(search)}
          placeholder="Search by brand or model…"
          className="vd-input"
          style={{ border: "none", padding: 0, height: "auto" }}
          data-testid="admin-products-search-input"
        />
      </div>

      {err && <p className="text-sm mb-3" style={{ color: "var(--primary-red)" }}>{err}</p>}

      <div className="vd-card overflow-x-auto" data-testid="admin-products-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left vd-muted uppercase text-xs tracking-wider border-b" style={{ borderColor: "var(--border-color)" }}>
              <th className="px-4 py-3">Product</th>
              <th>Size</th>
              <th>Category</th>
              <th>Price</th>
              <th>Stock</th>
              <th>Visible</th>
              <th className="text-right pr-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="7" className="px-4 py-8 text-center vd-muted">Loading…</td></tr>
            ) : products.length === 0 ? (
              <tr><td colSpan="7" className="px-4 py-8 text-center vd-muted">No products.</td></tr>
            ) : products.map((p) => (
              <tr key={p.id} className="border-t" style={{ borderColor: "var(--border-color)" }} data-testid={`admin-product-row-${p.id}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    {p.images?.[0] && <img src={p.images[0]} alt="" className="w-10 h-10 rounded object-cover" style={{ background: "var(--bg-secondary)" }} />}
                    <div>
                      <p className="vd-heading font-medium">{p.brand} {p.model}</p>
                      <p className="vd-muted text-xs">{p.images?.length || 0} image(s)</p>
                    </div>
                  </div>
                </td>
                <td className="vd-muted">{p.size}</td>
                <td className="vd-muted">{p.category}</td>
                <td className="vd-heading font-semibold">£{p.price.toFixed(2)}{p.sale_price ? <span className="vd-muted text-xs ml-1">/ £{p.sale_price.toFixed(2)}</span> : null}</td>
                <td><span style={{ color: p.stock_qty < 5 ? "var(--primary-red)" : "var(--text-heading)", fontWeight: 600 }}>{p.stock_qty}</span></td>
                <td>
                  <span className={`inline-flex items-center gap-1 text-xs font-semibold rounded px-2 py-0.5`} style={{ background: p.is_visible ? "rgba(0,180,80,0.15)" : "rgba(230,0,0,0.10)", color: p.is_visible ? "#00A35C" : "var(--primary-red)" }}>
                    {p.is_visible ? "Live" : "Hidden"}
                  </span>
                </td>
                <td className="pr-4">
                  <div className="flex items-center gap-1 justify-end">
                    <Link to={`/admin/products/${p.id}`} className="p-2 rounded hover:bg-[var(--bg-secondary)]" title="Edit" data-testid={`product-edit-${p.id}`}><Pencil size={14} /></Link>
                    <button onClick={() => doDuplicate(p.id)} className="p-2 rounded hover:bg-[var(--bg-secondary)]" title="Duplicate" data-testid={`product-duplicate-${p.id}`}><Copy size={14} /></button>
                    <button onClick={() => doToggle(p.id)} className="p-2 rounded hover:bg-[var(--bg-secondary)]" title="Toggle visibility" data-testid={`product-toggle-${p.id}`}>
                      {p.is_visible ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                    <button onClick={() => doDelete(p.id)} className="p-2 rounded hover:bg-[var(--bg-secondary)]" title="Delete" data-testid={`product-delete-${p.id}`}><Trash2 size={14} color="var(--primary-red)" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminProducts;
